import React, { useState, useCallback, useRef, useEffect } from 'react';
import './InlineImageViewer.css';
import config from '../utils/config';

const InlineImageViewer = ({ file, folderId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showControls, setShowControls] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Professional SVG Icons
  const ZoomInIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.35-4.35"/>
      <line x1="8" y1="11" x2="14" y2="11"/>
      <line x1="11" y1="8" x2="11" y2="14"/>
    </svg>
  );

  const ZoomOutIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.35-4.35"/>
      <line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  );

  const ResetIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M3 21v-5h5"/>
    </svg>
  );


  // Load image URL
  useEffect(() => {
    if (!file || !folderId) {
      setError('Missing file information');
      setLoading(false);
      return;
    }

    const fileId = file.fileId || file.file_id;
    const url = `${config.apiUrl}/api/preview/${folderId}/${fileId}`;
    
    console.log('Loading inline image:', {
      filename: file.originalFilename || file.original_filename || file.filename,
      url
    });

    // Fast path: let the browser stream the image directly
    setImageUrl(url);
    setError(null);
    // No cleanup needed for direct URL
    return () => {};
  }, [file, folderId]);

  // Control functions
  const resetView = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.5, 5));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.5, 0.5));
  }, []);


  // Drag and pan functionality
  const handleMouseDown = useCallback((e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for mobile
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1 && zoom > 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  }, [zoom, position]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isDragging && zoom > 1) {
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoom]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Double click to zoom
  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    if (zoom === 1) {
      setZoom(2);
      setPosition({ x: 0, y: 0 });
    } else {
      resetView();
    }
  }, [zoom, resetView]);

  // Hide controls after inactivity
  useEffect(() => {
    let timer;
    const hideControls = () => {
      timer = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    const showControlsHandler = () => {
      setShowControls(true);
      if (timer) clearTimeout(timer);
      hideControls();
    };

    document.addEventListener('mousemove', showControlsHandler);
    document.addEventListener('touchstart', showControlsHandler);
    hideControls();

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('mousemove', showControlsHandler);
      document.removeEventListener('touchstart', showControlsHandler);
    };
  }, []);

  // Add event listeners for dragging
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  if (loading && !imageUrl) {
    return (
      <div className="inline-image-loading">
        <div className="loading-spinner"></div>
        <p>Loading image...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inline-image-error">
        <div className="error-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
          </svg>
        </div>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="inline-image-viewer">
      <div 
        ref={containerRef}
        className={`inline-image-container ${loading ? 'loading' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={handleDoubleClick}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt=""
          className="inline-image"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onLoad={() => setLoading(false)}
          onError={() => {
            setError('Failed to load image');
            setLoading(false);
          }}
          draggable={false}
        />

        {/* Zoom indicator */}
        <div className={`inline-zoom-indicator ${zoom !== 1 ? 'visible' : ''}`}>
          {Math.round(zoom * 100)}%
        </div>

        {/* Professional control buttons */}
        <div className={`inline-image-controls ${showControls ? 'visible' : ''}`}>
          <button 
            className="inline-control-btn" 
            onClick={zoomOut}
            disabled={zoom <= 0.5}
            title="Zoom Out"
            aria-label="Zoom Out"
          >
            <ZoomOutIcon />
          </button>
          <button 
            className="inline-control-btn" 
            onClick={resetView}
            title="Reset View"
            aria-label="Reset View"
          >
            <ResetIcon />
          </button>
          <button 
            className="inline-control-btn" 
            onClick={zoomIn}
            disabled={zoom >= 5}
            title="Zoom In"
            aria-label="Zoom In"
          >
            <ZoomInIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(InlineImageViewer); 